/**
 * Los renglones que se abren adentro de un modelo: una memoria cada uno.
 *
 * QUE CAMBIA
 * ----------
 * Hasta ahora el desplegable listaba las fichas cargadas y nada más. Desplegar
 * el 18 Pro Max mostraba un solo renglón —el de 256 GB— y las otras memorias
 * no aparecían por ningún lado: no estaban ocultas, nunca se habían creado.
 *
 * Es la misma falla que el lineup arregló un nivel más arriba. La fila del
 * modelo existe aunque no haya nada cargado, y dice «Sin cargar»; adentro, en
 * cambio, lo que faltaba era invisible. Y justo adentro es donde está la plata:
 * la memoria es lo que mueve el precio.
 *
 * Acá el esqueleto también es el lineup. Los renglones salen de
 * `MEMORIAS_POR_MODELO` —lo que Apple hizo— y encima se apoyan las fichas que
 * existen. Un renglón sin ficha no es un error: es el hueco, y se llena
 * cargándole el costo y el precio ahí mismo.
 *
 * QUE NO SE PIERDE
 * ----------------
 * Una ficha cargada SIEMPRE tiene renglón, aunque su capacidad no esté en la
 * tabla. Pasa con los nombres viejos que no dicen la memoria (capacidad 0) y
 * pasaría con una capacidad que el lineup no conozca. Antes que esconder algo
 * que alguien cargó, se muestra donde estaba: la tabla manda sobre lo que
 * FALTA, nunca sobre lo que hay. Es la misma regla que el lineup de modelos.
 */
import { calcularPrecio, despejarPrecio, type Moneda } from "@tora/shared";

import { aNumero, type NuevoProducto, type Producto } from "@/lib/tipos";

import { nombreDeEquipo } from "./equipo";
import { parsearIphone, stockConUnidades } from "./logica";
import { memoriasDelModelo } from "./memorias";

// ─────────────────────────────────────────────────────────────────────────────
// La clave de una memoria
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Como se nombra una memoria del lineup: "17 PRO MAX|512".
 *
 * La barra separa porque un modelo nunca la tiene, así que la clave se parte
 * sin ambigüedad aunque el modelo tenga espacios. Los gigas van como se
 * guardan en `producto.memoria_gb`: 1 TB es 1024.
 */
export function claveDeMemoria(modelo: string, memoriaGb: number): string {
  return `${modelo.trim().toUpperCase().replace(/\s+/g, " ")}|${memoriaGb}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Un renglón
// ─────────────────────────────────────────────────────────────────────────────

export interface RenglonDeMemoria {
  /** "17 PRO MAX|512". Estable aunque todavía no exista ninguna ficha. */
  clave: string;
  /** El modelo como lo escribe `parsearIphone`: "17 PRO MAX". */
  modelo: string;
  memoriaGb: number;
  /**
   * Las fichas de esta memoria. Normalmente una; ninguna si el renglón viene
   * del lineup y nadie lo cargó.
   *
   * Es una lista y no una sola porque pueden quedar dos fichas del mismo
   * modelo y la misma memoria —un duplicado que alguien tiene que resolver a
   * mano— y desaparecer una sería esconder justamente el problema.
   */
  fichas: Producto[];
  /**
   * Equipos de esta memoria que no tienen de qué colgar.
   *
   * Pasa en Usados: entra un 17 Pro Max de 256 y todavía no existe el precio
   * de referencia de ese modelo, así que el equipo queda suelto. Antes caía
   * en `fichas` junto con los demás y el renglón lo trataba como un duplicado
   * —«hay otra ficha de esta memoria»— cuando eran tres teléfonos distintos.
   * Peor: el primero pasaba a ser la memoria, y cargarle la plata al renglón
   * le pisaba el precio a ese equipo.
   *
   * No son la lista de precios: se listan abajo como cualquier equipo, cada
   * uno con su batería y su precio.
   */
  sueltos: Producto[];
  /** Cuántas unidades hay, contando las que cuelgan de cada ficha. */
  stock: number;
  /**
   * Otra memoria YA CARGADA del mismo modelo, si hay.
   *
   * De ahí salen la familia, la marca, el proveedor y la moneda cuando este
   * renglón se carga por primera vez: son datos del MODELO y no de la
   * capacidad, así que copiarlos es más fiel que volver a preguntarlos.
   */
  hermana: Producto | null;
  /** Esta memoria está marcada como «no la trabajo». */
  oculta: boolean;
  /** No hay ninguna ficha ni equipo: el renglón es el hueco del lineup. */
  vacio: boolean;
}

/**
 * Los renglones de un modelo: el lineup con las fichas encima.
 *
 * Las memorias van de menor a mayor —como se pregunta— y las que no se
 * trabajan quedan marcadas pero NO se sacan ni se reordenan acá: eso lo decide
 * quien dibuja, igual que con los modelos apagados.
 */
export function renglonesDeMemoria({
  modelo,
  memorias,
  unidades,
  ocultas,
  conLineup = true,
  esModelo = () => true,
}: {
  /** El modelo como lo escribe `parsearIphone`: "17 PRO MAX". */
  modelo: string;
  /** Las fichas cargadas de ese modelo, una por memoria. */
  memorias: ReadonlyArray<Producto>;
  unidades: ReadonlyMap<number, Producto[]>;
  /** Las memorias marcadas como «no las trabajo», ya sin prefijo de sección. */
  ocultas?: ReadonlySet<string>;
  /**
   * Si se agregan las memorias del lineup que no están cargadas.
   *
   * Se apaga por lo mismo que en la lista de modelos: con un filtro puesto
   * —«con stock», «ver solo los incompletos»— la persona está buscando algo
   * puntual, y un renglón que no existe no tiene stock ni datos que arreglar.
   */
  conLineup?: boolean;
  /**
   * Si una ficha es de la lista de precios o un equipo concreto. Lo decide la
   * sección: en sellados, el modelo es el que no tiene color; en usados, el
   * precio de referencia. Sin esto, todas se toman como de la lista.
   */
  esModelo?: (p: Producto) => boolean;
}): RenglonDeMemoria[] {
  const porCapacidad = new Map<number, Producto[]>();
  const sueltosPorCapacidad = new Map<number, Producto[]>();
  for (const m of memorias) {
    const gb = m.memoria_gb ?? parsearIphone(m.nombre).capacidadGb;
    const destino = esModelo(m) ? porCapacidad : sueltosPorCapacidad;
    destino.set(gb, [...(destino.get(gb) ?? []), m]);
  }

  const capacidades = [
    ...new Set([
      ...(conLineup ? memoriasDelModelo(modelo) : []),
      ...porCapacidad.keys(),
      ...sueltosPorCapacidad.keys(),
    ]),
  ].sort((a, b) => a - b);

  // Solo de la lista de precios: un equipo suelto tiene el proveedor y la
  // moneda de ESE teléfono, no los del modelo.
  const hermana = hermanaDe(memorias.filter(esModelo));

  return capacidades.map((gb) => {
    const fichas = porCapacidad.get(gb) ?? [];
    const sueltos = sueltosPorCapacidad.get(gb) ?? [];
    return {
      clave: claveDeMemoria(modelo, gb),
      modelo,
      memoriaGb: gb,
      fichas,
      sueltos,
      stock: [...fichas, ...sueltos].reduce((t, f) => t + stockConUnidades(f, unidades), 0),
      hermana,
      oculta: ocultas?.has(claveDeMemoria(modelo, gb)) ?? false,
      vacio: fichas.length === 0 && sueltos.length === 0,
    };
  });
}

/**
 * De qué colores hay ALGO en este renglón.
 *
 * Normalmente son los de los equipos que cuelgan de la ficha. Pero una ficha
 * puede tener color propio y no colgar de nadie —un usado del 17 Pro Max
 * cargado sin que exista el precio de referencia del modelo— y ahí el color es
 * el de ella misma.
 *
 * No devuelve la gama del modelo: eso se contesta una vez arriba, en la
 * columna COLOR de la fila del modelo. Acá la pregunta es otra —«¿de cuáles
 * tenés?»— y repetir la gama por memoria la tapa.
 */
export function coloresDelRenglon(
  renglon: RenglonDeMemoria,
  unidades: ReadonlyMap<number, Producto[]>,
): Producto[] {
  const deLasFichas = renglon.fichas.flatMap((f) => {
    const equipos = unidades.get(f.id) ?? [];
    if (equipos.length > 0) return equipos;
    return f.color ? [f] : [];
  });
  return [...deLasFichas, ...renglon.sueltos.filter((s) => s.color)];
}

// ─────────────────────────────────────────────────────────────────────────────
// Cargarle el precio a una memoria que todavía no existe
// ─────────────────────────────────────────────────────────────────────────────

/** Lo que se carga en un renglón: las dos puntas de la plata. */
export interface PreciosDeMemoria {
  costo: number | null;
  costoMoneda: Moneda;
  precio: number | null;
  precioMoneda: Moneda;
}

/**
 * Con qué valores abre el renglón.
 *
 * Si la ficha existe, con los suyos. Si no existe, en blanco pero con la
 * moneda de una hermana —las memorias de un modelo se compran todas en la
 * misma— y si no hay ninguna, en dólares, que es como se compra un sellado.
 */
export function preciosDeRenglon(
  ficha: Producto | null,
  hermana: Producto | null,
): PreciosDeMemoria {
  if (ficha) {
    const costo = aNumero(ficha.costo);
    const precio = ficha.precio_manual === null ? null : aNumero(ficha.precio_manual);
    return {
      costo: costo > 0 ? costo : null,
      costoMoneda: ficha.costo_moneda,
      precio: precio !== null && precio > 0 ? precio : null,
      precioMoneda: ficha.precio_moneda,
    };
  }
  return {
    costo: null,
    costoMoneda: hermana?.costo_moneda ?? "USD",
    precio: null,
    precioMoneda: hermana?.precio_moneda ?? "USD",
  };
}

/**
 * ¿La ficha está congelada porque alguien lo quiso?
 *
 * Una ficha con `precio_manual` cuyo margen YA da ese mismo precio no está
 * fijada a propósito: es un resto del error viejo del renglón, que mandaba
 * todo precio escrito a `precio_manual` (AP-045, AP-048 y AP-050, con FIJO 105,
 * quedaron así). Antes se la tomaba por fijada, y toda memoria nueva de esos
 * modelos nacía congelada y no acompañaba al costo. Si el margen no se puede
 * calcular (sin costo, o monedas distintas sin cotización), se respeta el fijo.
 */
function fijadaAProposito(p: Producto, cotizacion: number): boolean {
  if (p.precio_manual === null) return false;
  const costo = aNumero(p.costo);
  if (!(costo > 0)) return true;
  const hayQueConvertir =
    p.costo_moneda !== p.precio_moneda ||
    (p.markup_tipo === "FIJO" && (p.markup_moneda ?? p.precio_moneda) !== p.precio_moneda);
  if (hayQueConvertir && !(cotizacion > 0)) return true;
  try {
    const { precioVenta } = calcularPrecio({
      costo,
      costoMoneda: p.costo_moneda,
      markupTipo: p.markup_tipo,
      markupValor: aNumero(p.markup_valor),
      markupMoneda: p.markup_moneda ?? p.precio_moneda,
      precioMoneda: p.precio_moneda,
      redondeo: p.redondeo,
      cotizacion,
    });
    return precioVenta !== aNumero(p.precio_manual);
  } catch {
    return true;
  }
}

/**
 * El precio escrito en el renglón, como lo guarda la ficha.
 *
 * ESCRIBIR EL PRECIO NO ES CONGELARLO (pendientes, decisión 4). Antes los dos
 * caminos del renglón mandaban el número a `precio_manual`: la memoria nueva
 * nacía congelada y con margen cero, el ingreso con un costo nuevo no la
 * repreciaba, y cambiarle el precio a una ficha que andaba con margen (la
 * AP-047, con FIJO 105) la dejaba tildada en «Fijar este precio» sin que nadie
 * lo pidiera. Ahora, con costo, se despeja el margen que da ese precio —igual
 * que el alta y la ficha—, con el tipo de margen y el redondeo de la ficha (o
 * de la hermana, si es nueva).
 *
 * Queda fijo en los casos en que ya lo estaba o no hay de dónde despejar: la
 * ficha (o la hermana) congelada a propósito (`fijadaAProposito`), sin costo,
 * o costo y precio en monedas distintas sin cotización.
 */
function precioDelRenglon(
  precios: PreciosDeMemoria,
  referencia: Producto | null,
  cotizacion: number,
): Pick<
  NuevoProducto,
  "precio_manual" | "markup_tipo" | "markup_valor" | "markup_moneda" | "redondeo"
> {
  const fijo = { precio_manual: precios.precio };
  if (precios.precio === null) return fijo;
  if (referencia && fijadaAProposito(referencia, cotizacion)) return fijo;
  if (!(precios.costo !== null && precios.costo > 0)) return fijo;
  if (precios.costoMoneda !== precios.precioMoneda && !(cotizacion > 0)) return fijo;
  const markupTipo = referencia?.markup_tipo ?? "PORCENTAJE";
  const markupMoneda =
    markupTipo === "FIJO" ? (referencia?.markup_moneda ?? precios.precioMoneda) : undefined;
  const despeje = despejarPrecio({
    costo: precios.costo,
    costoMoneda: precios.costoMoneda,
    precioDeseado: precios.precio,
    precioMoneda: precios.precioMoneda,
    markupTipo,
    ...(markupMoneda ? { markupMoneda } : {}),
    redondeo: referencia?.redondeo ?? "NINGUNO",
    cotizacion,
  });
  if (despeje === null) return fijo;
  return {
    precio_manual: null,
    markup_tipo: markupTipo,
    markup_valor: despeje.markupValor,
    ...(markupMoneda ? { markup_moneda: markupMoneda } : {}),
    redondeo: despeje.redondeo,
  };
}

/**
 * La ficha que se crea al cargarle los precios a una memoria vacía.
 *
 * ES UN RENGLON DE LISTA DE PRECIOS, NO UN EQUIPO. Nace sin color y en cero, y
 * tiene que ser así: el color se elige cuando entra un equipo de verdad, y el
 * stock lo mueve un ingreso. Poner cualquiera de los dos acá sería inventar
 * que hay un teléfono en el local porque alguien anotó cuánto sale.
 *
 * El precio escrito se guarda como el margen que lo da (`precioDelRenglon`).
 *
 * Familia, marca y proveedor se copian de una hermana —otra memoria del mismo
 * modelo— porque son del modelo y no de la capacidad. Sin hermana quedan
 * vacíos: antes que adivinar una familia, la ficha nace sin ella y el catálogo
 * lo marca como pendiente.
 *
 * NACE PUBLICADA SI EL MODELO Y LA MEMORIA SE TRABAJAN. La regla es «visible
 * en el lineup = publicado» (0082): nacía con `publicado = false` y ningún
 * trigger la publicaba al insertarse, así que el mostrador la ofrecía a pedido
 * y la tienda no la mostraba. `oculta` es la marca del renglón («no la
 * trabajo»), que ya suma la del modelo.
 */
export function fichaDeMemoria({
  modelo,
  memoriaGb,
  esUsado,
  hermana,
  precios,
  oculta = false,
  cotizacion = 0,
}: {
  modelo: string;
  memoriaGb: number;
  esUsado: boolean;
  hermana: Producto | null;
  precios: PreciosDeMemoria;
  oculta?: boolean;
  cotizacion?: number;
}): NuevoProducto {
  return {
    nombre: nombreDeEquipo({ modelo, memoriaGb, esUsado }),
    color: null,
    memoria_gb: memoriaGb,
    es_usado: esUsado,
    rubro_id: hermana?.rubro_id ?? null,
    marca: hermana?.marca ?? null,
    proveedor_id: hermana?.proveedor_id ?? null,
    costo: precios.costo ?? 0,
    costo_moneda: precios.costoMoneda,
    precio_moneda: precios.precioMoneda,
    ...precioDelRenglon(precios, hermana, cotizacion),
    publicado: !oculta,
    // En cero se consigue: es un renglón de la lista, no un faltante. Sin
    // esto el mostrador dice «Sin stock» y venderlo lo anota como faltante en
    // vez de encargarlo (0037). Si no se trabaja, tampoco se promete (0082).
    a_pedido: !oculta,
  };
}

/**
 * Lo que hay que mandarle a una ficha que ya existe para cambiarle la plata.
 * El precio, como en la ficha nueva: el margen que lo da, salvo que la ficha
 * ya estuviera congelada.
 */
export function cambiosDeMemoria(
  precios: PreciosDeMemoria,
  ficha: Producto | null = null,
  cotizacion = 0,
): Partial<NuevoProducto> {
  return {
    costo: precios.costo ?? 0,
    costo_moneda: precios.costoMoneda,
    precio_moneda: precios.precioMoneda,
    ...precioDelRenglon(precios, ficha, cotizacion),
  };
}

/**
 * Otra memoria del mismo modelo, para copiarle lo que es del modelo.
 *
 * La primera que tenga familia cargada; si ninguna la tiene, la primera. Se
 * prefiere una con familia porque es el dato que después hace falta para que
 * la ficha nueva caiga en la sección correcta.
 */
export function hermanaDe(memorias: ReadonlyArray<Producto>): Producto | null {
  return memorias.find((m) => m.rubro_id !== null) ?? memorias[0] ?? null;
}
